import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';
import type { AnimalMediaItem } from '@/types/api';
import { ChevronLeft, ChevronRight, Video, X } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';

export type MediaLightboxEntry = Pick<AnimalMediaItem, 'id' | 'media'>;

type MediaLightboxProps = {
  open: boolean;
  onClose: () => void;
  items: MediaLightboxEntry[];
  activeId: number | null;
  onActiveChange: (id: number) => void;
  videoPosterUrl?: string;
  actions?: (item: MediaLightboxEntry) => React.ReactNode;
};

export function MediaLightbox({
  open,
  onClose,
  items,
  activeId,
  onActiveChange,
  videoPosterUrl,
  actions,
}: MediaLightboxProps) {
  const activeIndex = useMemo(
    () => (activeId == null ? -1 : items.findIndex((item) => item.id === activeId)),
    [activeId, items],
  );
  const active = activeIndex >= 0 ? items[activeIndex] : null;

  const goPrev = useCallback(() => {
    if (activeIndex <= 0) return;
    onActiveChange(items[activeIndex - 1].id);
  }, [activeIndex, items, onActiveChange]);

  const goNext = useCallback(() => {
    if (activeIndex < 0 || activeIndex >= items.length - 1) return;
    onActiveChange(items[activeIndex + 1].id);
  }, [activeIndex, items, onActiveChange]);

  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };

    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose, goPrev, goNext]);

  if (!open || !active) return null;

  const isVideo = active.media.media_kind === 'video';
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < items.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/95"
      role="dialog"
      aria-modal
      aria-label={isVideo ? 'Visualizar vídeo' : 'Ampliar foto'}
    >
      <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3 text-white">
        <div className="min-w-0 text-sm text-white/75">
          {items.length > 1 && (
            <span>
              {activeIndex + 1} / {items.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20"
          aria-label="Fechar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-2">
        {hasPrev && (
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70 sm:left-4"
            aria-label="Mídia anterior"
          >
            <ChevronLeft className="h-7 w-7" />
          </button>
        )}

        <div className="flex h-full w-full max-w-6xl items-center justify-center">
          {isVideo ? (
            <video
              key={active.id}
              src={active.media.url}
              poster={videoPosterUrl}
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="max-h-[calc(100vh-10rem)] max-w-full rounded-lg"
            />
          ) : (
            <img
              key={active.id}
              src={active.media.url}
              alt="Captura do animal"
              className="max-h-[calc(100vh-10rem)] max-w-full object-contain select-none"
              draggable={false}
            />
          )}
        </div>

        {hasNext && (
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70 sm:right-4"
            aria-label="Próxima mídia"
          >
            <ChevronRight className="h-7 w-7" />
          </button>
        )}
      </div>

      <div className="shrink-0 space-y-3 border-t border-white/10 px-4 py-4 text-white">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/70">
          <span className="inline-flex items-center gap-1">
            {isVideo && <Video className="h-3.5 w-3.5" />}
            {active.media.captured_at ? formatDateTime(active.media.captured_at) : '—'}
          </span>
          {active.media.uploaded_by && <span>Por {active.media.uploaded_by.name}</span>}
          {typeof active.media.metadata?.city === 'string' && (
            <span>
              {String(active.media.metadata.city)}
              {typeof active.media.metadata?.state === 'string' ? `/${active.media.metadata.state}` : ''}
            </span>
          )}
        </div>
        {actions && (
          <div className={cn('flex flex-wrap justify-end gap-2')}>{actions(active)}</div>
        )}
      </div>
    </div>
  );
}
